import React from 'react';
import { useTranslation } from 'react-i18next';
import { RefreshCcw, CheckCircle2, AlertCircle, HelpCircle, Package, Truck, CreditCard } from 'lucide-react';

const Returns = () => {
  const { t } = useTranslation();

  return (
    <div className="min-h-screen bg-raya-bg py-20 px-4 transition-colors duration-300">
      <div>
        <div className="text-center mb-16">
          <div className="inline-flex items-center justify-center p-3 bg-raya-accent/10 rounded-2xl mb-4">
            <RefreshCcw className="w-8 h-8 text-raya-accent" />
          </div>
          <h1 className="text-4xl md:text-5xl font-bold text-raya-text-primary mb-4">
            {t('returnPolicy.title')}
          </h1>
          <p className="text-raya-text-tertiary">
            {t('returnPolicy.lastUpdated')}
          </p>
        </div>

        <div className="bg-raya-card/50 backdrop-blur-xl border border-raya-border rounded-3xl p-8 md:p-12 shadow-xl">
          <p className="text-lg text-raya-text-secondary mb-12 leading-relaxed">
            {t('returnPolicy.intro')}
          </p>

          <div className="grid gap-12">
            {/* Eligibility Section */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <CheckCircle2 className="w-6 h-6 text-raya-accent" />
                <h2 className="text-2xl font-bold text-raya-text-primary">
                  {t('returnPolicy.section1Title')}
                </h2>
              </div>
              <div className="space-y-4 ml-9">
                <p className="text-raya-text-secondary">{t('returnPolicy.section1Text')}</p>
                <ul className="list-disc list-inside space-y-2 text-raya-text-secondary ml-4">
                  <li>{t('returnPolicy.eligibility1')}</li>
                  <li>{t('returnPolicy.eligibility2')}</li>
                  <li>{t('returnPolicy.eligibility3')}</li>
                </ul>
              </div>
            </section>

            {/* Non-Returnable Section */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <AlertCircle className="w-6 h-6 text-raya-accent" />
                <h2 className="text-2xl font-bold text-raya-text-primary">
                  {t('returnPolicy.section2Title')}
                </h2>
              </div>
              <div className="space-y-4 ml-9">
                <p className="text-raya-text-secondary">{t('returnPolicy.section2Text')}</p>
                <ul className="list-disc list-inside space-y-2 text-raya-text-secondary ml-4">
                  <li>{t('returnPolicy.nonReturnable1')}</li>
                  <li>{t('returnPolicy.nonReturnable2')}</li>
                  <li>{t('returnPolicy.nonReturnable3')}</li>
                </ul>
              </div>
            </section>

            {/* Process Section */}
            <section>
              <div className="flex items-center gap-3 mb-6">
                <Package className="w-6 h-6 text-raya-accent" />
                <h2 className="text-2xl font-bold text-raya-text-primary">
                  {t('returnPolicy.section3Title')}
                </h2>
              </div>
              <div className="grid md:grid-cols-3 gap-6 ml-9">
                <div className="p-4 bg-raya-bg-off rounded-2xl border border-raya-border">
                  <div className="w-8 h-8 bg-raya-bg rounded-lg flex items-center justify-center text-sm font-bold mb-3">1</div>
                  <p className="text-sm text-raya-text-secondary">{t('returnPolicy.section3Step1')}</p>
                </div>
                <div className="p-4 bg-raya-bg-off rounded-2xl border border-raya-border">
                  <div className="w-8 h-8 bg-raya-bg rounded-lg flex items-center justify-center text-sm font-bold mb-3">2</div>
                  <p className="text-sm text-raya-text-secondary">{t('returnPolicy.section3Step2')}</p>
                </div>
                <div className="p-4 bg-raya-bg-off rounded-2xl border border-raya-border">
                  <div className="w-8 h-8 bg-raya-bg rounded-lg flex items-center justify-center text-sm font-bold mb-3">3</div>
                  <p className="text-sm text-raya-text-secondary">{t('returnPolicy.section3Step3')}</p>
                </div>
              </div>
            </section>

            <div className="grid md:grid-cols-2 gap-8 mt-4">
              {/* Refunds */}
              <div className="p-6 bg-raya-accent/5 rounded-3xl border border-raya-accent/20">
                <div className="flex items-center gap-3 mb-4">
                  <CreditCard className="w-6 h-6 text-raya-accent font-bold" />
                  <h3 className="text-xl font-bold text-raya-text-primary">{t('returnPolicy.section4Title')}</h3>
                </div>
                <p className="text-raya-text-secondary text-sm leading-relaxed">
                  {t('returnPolicy.section4Text')}
                </p>
              </div>

              {/* Exchanges */}
              <div className="p-6 bg-raya-bg-off rounded-3xl border border-raya-border">
                <div className="flex items-center gap-3 mb-4">
                  <RefreshCcw className="w-6 h-6 text-raya-text-secondary font-bold" />
                  <h3 className="text-xl font-bold text-raya-text-primary">{t('returnPolicy.section5Title')}</h3>
                </div>
                <p className="text-raya-text-secondary text-sm leading-relaxed">
                  {t('returnPolicy.section5Text')}
                </p>
              </div>
            </div>

            {/* Shipping Costs */}
            <section className="bg-raya-card p-8 rounded-3xl border border-raya-accent/30 transition-colors duration-300 relative overflow-hidden group">
              <div className="absolute top-0 right-0 w-32 h-32 bg-raya-accent/5 rounded-full -mr-16 -mt-16 group-hover:scale-110 transition-transform duration-700"></div>
              <div className="relative z-10">
                <div className="flex items-center gap-4 mb-4">
                  <div className="p-2 bg-raya-accent/10 rounded-xl group-hover:bg-raya-accent/20 transition-colors">
                    <Truck className="w-6 h-6 text-raya-accent" />
                  </div>
                  <h2 className="text-2xl font-bold text-raya-text-primary">{t('returnPolicy.section6Title')}</h2>
                </div>
                <p className="text-raya-text-primary/80 leading-relaxed font-medium">
                  {t('returnPolicy.section6Text')}
                </p>
              </div>
            </section>

            {/* Contact Section */}
            <section className="text-center pt-8 border-t border-raya-border">
              <div className="inline-flex items-center justify-center w-12 h-12 bg-raya-bg-off rounded-xl mb-4">
                <HelpCircle className="w-6 h-6 text-raya-text-secondary" />
              </div>
              <h2 className="text-2xl font-bold text-raya-text-primary mb-3">
                {t('returnPolicy.section7Title')}
              </h2>
              <p className="text-raya-text-secondary mb-6 max-w-lg mx-auto">
                {t('returnPolicy.section7Text')}
              </p>
              <a 
                href="/contact" 
                className="inline-flex items-center px-8 py-4 bg-raya-accent hover:bg-raya-accent-hover text-black font-extrabold rounded-2xl transition-all shadow-lg shadow-raya-accent/25 active:scale-95 transform hover:-translate-y-1"
              >
                {t('contact.title')}
              </a>
            </section>
          </div>
        </div>
      </div>
    </div>
  );
};

export default Returns;
